import {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import {
  AutocompleteOptions,
  DateState,
  MainSectionLayout,
  ReportConfig,
  FilterStateType,
  ReportData,
  FullScreenLoader,
} from '@Components/index';
import { RoutesConstants, CommonConstants } from '@Constants/index';
import CallLog from '@Containers/Report/CallLog';
import DialSessions from '@Containers/Report/DialSessions';
import EmailLog from '@Containers/Report/EmailLog';
import { useGetSessionsQuery } from '@Services/report.service';
import { BaseAlertProps, ReportTypes } from '@Typings/global.types';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import useRoute from '@Utils/hooks/useRoute.hook';
import { getSessions } from '@Utils/report/report.utils';

const ReportConfiguration: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { changeRoute } = useRoute();
  const { triggerAlert } = useAlert();

  /** PAGE HEADING */
  const heading = t('reports.Report Configuration');

  /** Memoised Initial Filter State
   * This will prevent re-render when used in useEffect as a dependency
   */
  const initialFilterState: FilterStateType = useMemo(
    () => ({
      status: [],
      disposition: [],
      callStatus: [],
    }),
    []
  );

  /** STATES */
  const [reportType, setReportType] = useState<ReportTypes | null>(
    query.get(CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE) as ReportTypes
  );
  const [dateState, setDateState] = useState<[DateState]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);
  const [filterState, setFilterState] =
    useState<FilterStateType>(initialFilterState);
  const [sessionAutocompleteValue, setSessionAutocompleteValue] = useState<
    AutocompleteOptions[]
  >([]);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  /** RTK QUERY HOOK */
  const { data, isFetching, isLoading, error } = useGetSessionsQuery([]);

  /** EDGE CASE
   * Adds default query params to the url if not added (pageNo, pageSize, order, direction).
   * */
  useEffect(() => {
    if (reportType) {
      const routeParams = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) ||
          CommonConstants.INITIAL_PAGE_NO,
        [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) ||
          CommonConstants.INITIAL_ROWS_PER_PAGE,
        ...(reportType === ReportTypes.Sessions && {
          [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
            query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER) ||
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DATE,
          [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
            query.get(CommonConstants.QUERY_PARAMS.COMMON.DIRECTION) ||
            CommonConstants.INITIAL_SORT_DIRECTION,
        }),
      });

      changeRoute(routeParams);
    }
  }, [changeRoute, reportType]);

  /** Show error for get query */
  useEffect(() => {
    if (error) {
      triggerAlert(error, setAlertState);
    }
  }, [error, triggerAlert]);

  /** Autocomplete Sessions Filter Options */
  const options = getSessions(data);

  /** Handles Reset Btn Click (resets all the filters) */
  const resetFilters = useCallback(() => {
    setFilterState({ ...initialFilterState });
    setDateState((prevState) => [
      { ...prevState[0], startDate: undefined, endDate: undefined },
    ]);
    setSessionAutocompleteValue([]);
  }, [initialFilterState]);

  useEffect(() => {
    setReportType(
      query.get(CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE) as ReportTypes
    );

    if (!reportType) {
      history.push(RoutesConstants.REPORT_ROUTES.BASE);
      resetFilters();
    }
  }, [location.search, history, reportType, resetFilters]);

  /** Changes the reportType with selected report filters & type and updates query params. */
  const generateBtnClickHandler = (reportData: ReportData) => {
    const {
      type,
      sessions,
      filters: { callStatus, status, disposition },
      startDate,
      endDate,
    } = reportData;

    setReportType(type);

    const searchParam = query.setAll({
      [CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE]: type,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
        CommonConstants.INITIAL_ROWS_PER_PAGE,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
        CommonConstants.INITIAL_PAGE_NO,
      [CommonConstants.QUERY_PARAMS.REPORT.SESSION_ID]: sessions,
      [CommonConstants.QUERY_PARAMS.REPORT.DISPOSITION]: disposition,
      [CommonConstants.QUERY_PARAMS.REPORT.CALL_STATUS]: callStatus,
      [CommonConstants.QUERY_PARAMS.COMMON.LIST_TYPE]: status,
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]:
        startDate?.toISOString() || '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]:
        endDate?.toISOString() || '',
      ...(type === ReportTypes.Sessions && {
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DATE,
        [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
          CommonConstants.INITIAL_SORT_DIRECTION,
      }),
    });

    changeRoute(searchParam);
  };

  /** New Report Button Click Handler */
  const generateNewReport = () => {
    changeRoute();
    setReportType(null);
  };

  /** Renders Report Container according to Report Type (CallLog Report/Session Summary) */
  const getReportScreen = () => {
    switch (reportType) {
      case ReportTypes.CallLog:
        return <CallLog newReportBtnProps={{ onClick: generateNewReport }} />;
      case ReportTypes.Sessions:
        return (
          <DialSessions newReportBtnProps={{ onClick: generateNewReport }} />
        );
      case ReportTypes.EmailLog:
        return <EmailLog newReportBtnProps={{ onClick: generateNewReport }} />;
      default:
        return (
          <MainSectionLayout
            alertState={alertState}
            heading={heading}
            filters={
              <ReportConfig
                type="report"
                filterState={filterState}
                setFilterState={setFilterState}
                sessionAutocompleteProps={{
                  options,
                  limitTags: CommonConstants.LIMIT_TAGS,
                  sessionAutocompleteValue,
                  setSessionAutocompleteValue,
                  loading: isLoading || isFetching,
                }}
                dateRangeProps={{
                  dateState,
                  setDateState,
                }}
                primaryBtnProps={{
                  text: t('common.messages.Generate'),
                  onClick: generateBtnClickHandler,
                }}
                secondaryBtnProps={{
                  text: t('common.messages.Reset All'),
                  onClick: resetFilters,
                }}
              />
            }
          />
        );
    }
  };

  return isLoading ? <FullScreenLoader /> : getReportScreen();
};

export default ReportConfiguration;
